import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Icon from "../UI/Icon";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import {
  displayPrice,
  formatINR,
  getCategories,
  priceUnit,
  productPath,
  searchProducts,
} from "../../Service/catalog";
import { getPath, getCurrentCity } from "../../Service/endpoints";
import { addRecentSearch, clearRecentSearches, getRecentSearches } from "../../Service/recentSearches";
import { getCategoryMeta, isCategoryVisible } from "../User/Catalog/categoryMeta";

const MIN_CHARS = 2;
const MAX_PRODUCTS = 6;

/**
 * Search combobox with debounced product suggestions, category matches,
 * recent searches and full keyboard support (WAI-ARIA combobox pattern).
 * variant: "header" | "hero" | "panel" (panel = list always visible, for overlays)
 */
function SearchBar({ variant = "header", autoFocus = false, onNavigate, initialQuery = "", placeholder }) {
  const history = useHistory();
  const [query, setQuery] = useState(initialQuery);
  const [open, setOpen] = useState(variant === "panel");
  const [active, setActive] = useState(-1);
  const [results, setResults] = useState({ q: "", items: [], status: "idle" });
  const [categories, setCategories] = useState([]);
  const [recent, setRecent] = useState(getRecentSearches);
  const debounced = useDebouncedValue(query.trim(), 280);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listId = useRef(`v-search-${Math.random().toString(36).slice(2, 8)}`).current;
  const alwaysOpen = variant === "panel";
  const city = getCurrentCity();

  useEffect(() => {
    let alive = true;
    getCategories().then(
      (list) => alive && setCategories(list.filter((c) => isCategoryVisible(c.slug, city))),
      () => {}
    );
    return () => {
      alive = false;
    };
  }, [city]);

  // Only query the API after typing pauses; responses for stale queries are ignored.
  useEffect(() => {
    if (debounced.length < MIN_CHARS) {
      setResults({ q: debounced, items: [], status: "idle" });
      return undefined;
    }
    let alive = true;
    setResults((prev) => ({ q: debounced, items: prev.q === debounced ? prev.items : [], status: "loading" }));
    searchProducts(debounced).then(
      (items) => alive && setResults({ q: debounced, items, status: "done" }),
      () => alive && setResults({ q: debounced, items: [], status: "error" })
    );
    return () => {
      alive = false;
    };
  }, [debounced]);

  useEffect(() => {
    if (alwaysOpen) return undefined;
    const onPointer = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
    };
  }, [alwaysOpen]);

  const trimmed = query.trim();
  const typing = trimmed.length >= MIN_CHARS;

  const matchedCategories = useMemo(() => {
    if (!typing) return [];
    const q = trimmed.toLowerCase();
    return categories.filter((c) => c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q)).slice(0, 3);
  }, [categories, trimmed, typing]);

  // Flattened option list drives keyboard navigation.
  const options = useMemo(() => {
    if (!typing) {
      return [
        ...recent.map((text) => ({ type: "recent", key: `r-${text}`, text })),
        ...categories.map((c) => ({ type: "category", key: `c-${c.slug}`, category: c })),
      ];
    }
    const products = results.q === debounced ? results.items.slice(0, MAX_PRODUCTS) : [];
    return [
      ...matchedCategories.map((c) => ({ type: "category", key: `c-${c.slug}`, category: c })),
      ...products.map((p) => ({ type: "product", key: `p-${p.id || p.slug}`, product: p })),
      { type: "all", key: "all", text: trimmed },
    ];
  }, [typing, recent, categories, matchedCategories, results, debounced, trimmed]);

  useEffect(() => setActive(-1), [trimmed]);

  const go = (path) => {
    setOpen(alwaysOpen);
    if (inputRef.current) inputRef.current.blur();
    if (onNavigate) onNavigate();
    history.push(path);
  };

  const submit = (text) => {
    const q = String(text || "").trim();
    if (!q) return;
    addRecentSearch(q);
    setRecent(getRecentSearches());
    setQuery(q);
    go(`/search/${encodeURIComponent(q)}`);
  };

  const choose = (option) => {
    if (!option) return;
    if (option.type === "recent" || option.type === "all") submit(option.text);
    else if (option.type === "category") go(getPath(option.category.slug));
    else if (option.type === "product") {
      addRecentSearch(trimmed);
      const path = productPath(option.product);
      if (path) go(path);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActive((i) => (options.length ? (i + 1) % options.length : -1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => (options.length ? (i <= 0 ? options.length - 1 : i - 1) : -1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (active >= 0 && options[active]) choose(options[active]);
      else submit(query);
    } else if (event.key === "Escape" && !alwaysOpen) {
      if (open) {
        event.stopPropagation();
        setOpen(false);
      } else {
        setQuery("");
      }
    }
  };

  const showList = (open || alwaysOpen) && options.length > 0;
  const activeId = active >= 0 && options[active] ? `${listId}-${active}` : undefined;
  let index = -1;
  const optionProps = (option) => {
    index += 1;
    const i = index;
    return {
      id: `${listId}-${i}`,
      role: "option",
      "aria-selected": active === i,
      className: `v-search__option ${active === i ? "is-active" : ""}`,
      onMouseDown: (e) => e.preventDefault(),
      onMouseEnter: () => setActive(i),
      onClick: () => choose(option),
    };
  };

  const recentOptions = options.filter((o) => o.type === "recent");
  const categoryOptions = options.filter((o) => o.type === "category");
  const productOptions = options.filter((o) => o.type === "product");
  const allOption = options.find((o) => o.type === "all");
  const searching = typing && (results.status === "loading" || results.q !== debounced);

  return (
    <div ref={rootRef} className={`v-search v-search--${variant} ${showList ? "is-open" : ""}`}>
      <form
        role="search"
        className="v-search__field"
        onSubmit={(e) => {
          e.preventDefault();
          submit(query);
        }}
      >
        <Icon name="search" size={variant === "hero" ? 22 : 18} className="v-search__icon" />
        <input
          ref={inputRef}
          type="search"
          className="v-search__input"
          value={query}
          placeholder={placeholder || "Search sofas, beds, ACs, fridges…"}
          autoFocus={autoFocus}
          data-autofocus={autoFocus ? "true" : undefined}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          enterkeyhint="search"
          role="combobox"
          aria-label="Search rentals"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {query ? (
          <button
            type="button"
            className="v-search__clear"
            aria-label="Clear search"
            onClick={() => {
              setQuery("");
              if (inputRef.current) inputRef.current.focus();
            }}
          >
            <Icon name="close" size={16} />
          </button>
        ) : null}
        {variant === "hero" ? (
          <button type="submit" className="v-btn v-btn--primary v-search__submit">
            Search
          </button>
        ) : null}
      </form>

      <div className="v-search__popover" hidden={!showList && !alwaysOpen}>
        <ul id={listId} role="listbox" aria-label="Search suggestions" className="v-search__list">
          {!typing && recentOptions.length ? (
            <li role="presentation" className="v-search__group">
              <span className="v-search__group-title">
                Recent searches
                <button
                  type="button"
                  className="v-search__group-action"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    clearRecentSearches();
                    setRecent([]);
                  }}
                >
                  Clear
                </button>
              </span>
            </li>
          ) : null}
          {recentOptions.map((option) => (
            <li key={option.key} {...optionProps(option)}>
              <Icon name="history" size={18} className="v-search__lead" />
              <span className="v-search__text">{option.text}</span>
            </li>
          ))}

          {categoryOptions.length ? (
            <li role="presentation" className="v-search__group">
              <span className="v-search__group-title">{typing ? "Categories" : "Browse categories"}</span>
            </li>
          ) : null}
          {categoryOptions.map((option) => (
            <li key={option.key} {...optionProps(option)}>
              <Icon name="layers" size={18} className="v-search__lead" />
              <span className="v-search__text">{getCategoryMeta(option.category.slug, option.category.name).short}</span>
              <Icon name="arrowUpRight" size={16} className="v-search__trail" />
            </li>
          ))}

          {typing ? (
            <li role="presentation" className="v-search__group">
              <span className="v-search__group-title">Products</span>
            </li>
          ) : null}
          {typing && searching && !productOptions.length
            ? [0, 1, 2].map((n) => (
                <li role="presentation" key={`s-${n}`} className="v-search__option v-search__option--skeleton" aria-hidden="true">
                  <span className="v-skeleton v-search__thumb" />
                  <span className="v-search__text">
                    <span className="v-skeleton v-skeleton--text" style={{ width: `${70 - n * 12}%` }} />
                  </span>
                </li>
              ))
            : null}
          {productOptions.map((option) => {
            const price = displayPrice(option.product);
            return (
              <li key={option.key} {...optionProps(option)}>
                <span className="v-search__thumb">
                  {option.product.featuredImage ? (
                    <img src={option.product.featuredImage} alt="" width="44" height="44" loading="lazy" decoding="async" />
                  ) : null}
                </span>
                <span className="v-search__text">
                  <span className="v-search__name">{option.product.name}</span>
                  <span className="v-search__sub">
                    {option.product.category ? option.product.category.name : ""}
                    {price ? ` · ${formatINR(price)}/${priceUnit(option.product)}` : ""}
                  </span>
                </span>
              </li>
            );
          })}
          {typing && results.status === "done" && results.q === debounced && !productOptions.length ? (
            <li role="presentation" className="v-search__note">
              No products match “{trimmed}”. Try a broader term like “sofa” or “fridge”.
            </li>
          ) : null}
          {typing && results.status === "error" ? (
            <li role="presentation" className="v-search__note">
              Suggestions are unavailable right now — press Enter to search.
            </li>
          ) : null}
          {allOption ? (
            <li key={allOption.key} {...optionProps(allOption)}>
              <Icon name="search" size={18} className="v-search__lead" />
              <span className="v-search__text">
                See all results for <strong>“{allOption.text}”</strong>
              </span>
              <Icon name="arrowRight" size={16} className="v-search__trail" />
            </li>
          ) : null}
        </ul>
        <p className="v-sr-only" aria-live="polite">
          {typing && results.status === "done" && results.q === debounced
            ? `${Math.min(results.items.length, MAX_PRODUCTS)} product suggestions`
            : ""}
        </p>
      </div>
    </div>
  );
}

export default SearchBar;
